// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmailLine from "@store/Library/Components/Toolkit/Icons/Email/EmailLine";
import Img, { ImagePresentationProps } from "@store/MobiusOverrides/Img/Img";
import constants from "@store/constants";
import { LanguageMenuPresentationProps } from "@store/Overrides/Widgets/Common/LanguageMenu";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useWebsite } from "@toolkit/Framework/Store/Context/ContextHooks";
import { visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import React, { FC } from "react";
import { css } from "styled-components";

const LanguageMenu = getWidgetComponent("Common/LanguageMenu");

export interface SignedOutHeaderPresentationProps extends ExpandStyles<SignedOutHeaderStyles> {}

export interface SignedOutHeaderProps extends SignedOutHeaderPresentationProps {
    contactUsPageLink?: ReturnType<typeof useGetLink>;
    isProspectUser?: boolean;
    showContactUs?: boolean;
    logo?: string;
    logoAltText?: string;
}

export interface SignedOutHeaderStyles {
    wrapper?: InjectableCss;
    logoImage?: ImagePresentationProps;
    actionsWrapper?: InjectableCss;
    contactUsClickable?: ClickablePresentationProps;
    contactUsIcon?: IconPresentationProps;
    contactUsText?: TypographyPresentationProps;
    languageMenu?: LanguageMenuPresentationProps;
}

export const signedOutHeaderStyles: SignedOutHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            gap: ${pxToRem(24)};
        `,
    },
    logoImage: {
        css: css`
            display: block;
            height: ${pxToRem(32)};
            width: auto;

            ${breakpoint("sm")} {
                height: ${pxToRem(40)};
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-left: auto;
            gap: ${pxToRem(24)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    contactUsClickable: {
        css: css`
            ${linkStyles("inherit", null, false, true)}
            display: flex;
            align-items: center;
            gap: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
            font-weight: inherit;
        `,
    },
    contactUsIcon: {
        color: "inherit",
        size: 20,
        src: EmailLine,
    },
    contactUsText: {
        css: css`
            ${breakpoint("sm", "down")} {
                ${visuallyHiddenStyles()}
            }
        `,
    },
};

/**
 * The minimal header for the account application screens.
 */
const SignedOutHeader: FC<SignedOutHeaderProps> = ({
    contactUsPageLink,
    extendedStyles,
    isProspectUser,
    logo = constants.logoPath,
    logoAltText,
    overridedStyles,
    showContactUs,
}) => {
    const styles = useExpandProps(signedOutHeaderStyles, extendedStyles, overridedStyles);
    const website = useWebsite();

    return (
        <StyledWrapper {...styles.wrapper}>
            <Img {...styles.logoImage} loading="eager" src={logo} altText={logoAltText || website?.name || ""} />
            <StyledWrapper {...styles.actionsWrapper}>
                {showContactUs && (isProspectUser || contactUsPageLink?.url) && (
                    <Clickable
                        {...styles.contactUsClickable}
                        onClick={isProspectUser ? () => {} : undefined} // TODO: CDP-4296 - Contact us button click behavior
                        href={!isProspectUser ? contactUsPageLink?.url : undefined}
                        data-test-selector="signedOutHeader_ContactUs"
                    >
                        <Icon {...styles.contactUsIcon} />
                        <Typography {...styles.contactUsText}>{translate("Contact Us")}</Typography>
                    </Clickable>
                )}
                <LanguageMenu {...styles.languageMenu} fields={{ showIcon: true, hideLabelOnMobile: true }} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default SignedOutHeader;
